import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { peopleAPI } from '../services/api';
import { loadPhotoAsDataUrl } from './PhotoLoader';
import { ImageCropModal, CropData } from './ImageCropModal';
import { IconButton, SegmentedControl, TextInput, formErrorTextClassName } from './common';

interface PhotoUploadFieldProps {
  personId?: string;
  initialPhotoUrl?: string;
  initialCropData?: CropData | null;
  onPhotoUrlChange: (url: string) => void;
  onPhotoFileChange?: (file: File | null) => void;
  onCropDataChange?: (cropData: CropData | null) => void;
  onPhotoSaved?: (result: { photoUrl: string; cropData: CropData | null }) => void;
  disabled?: boolean;
  readOnly?: boolean;
  compact?: boolean;
  hideUrlField?: boolean;
  allowUrlUpload?: boolean;
  uploadLabel?: string;
}

export const PhotoUploadField: React.FC<PhotoUploadFieldProps> = ({
  personId,
  initialPhotoUrl = '',
  initialCropData = null,
  onPhotoUrlChange,
  onPhotoFileChange,
  onCropDataChange,
  onPhotoSaved,
  disabled = false,
  readOnly = false,
  compact = false,
  hideUrlField = false,
  allowUrlUpload = true,
  uploadLabel,
}) => {
  const { t } = useTranslation();
  const resolvedUploadLabel = uploadLabel ?? t('photo.uploadLabel', { ns: 'common' });
  const [photoUrl, setPhotoUrl] = useState(initialPhotoUrl);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadMode, setUploadMode] = useState<'url' | 'file'>('file');
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageForCrop, setImageForCrop] = useState<string>('');
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [editingExistingPhoto, setEditingExistingPhoto] = useState(false);
  const [lastCropData, setLastCropData] = useState<CropData | null>(initialCropData);  // Initialize from prop
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update when initialPhotoUrl or initialCropData changes (e.g., opening edit modal for different person)
  useEffect(() => {
    setPhotoUrl(initialPhotoUrl);
    setLastCropData(initialCropData);  // Sync crop data from prop
    setUploadMode(initialPhotoUrl && !readOnly && allowUrlUpload && !hideUrlField ? 'url' : 'file');

    // In read-only mode, load the photo data URL for preview
    if (readOnly && initialPhotoUrl) {
      loadPhotoAsDataUrl(initialPhotoUrl)
        .then(dataUrl => {
          setPreviewUrl(dataUrl);
        })
        .catch(err => {
          setPreviewUrl('');
        });
    } else {
      setPreviewUrl('');
    }
  }, [allowUrlUpload, hideUrlField, initialPhotoUrl, initialCropData, readOnly]);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File size must be less than 5MB (current: ${(file.size / 1024 / 1024).toFixed(2)}MB)`;
    }

    // Check MIME type
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `File type must be JPEG, PNG, or WebP (current: ${file.type})`;
    }

    // Check extension
    const fileName = file.name.toLowerCase();
    const hasValidExt = ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
    if (!hasValidExt) {
      return `File extension must be ${ALLOWED_EXTENSIONS.join(', ')}`;
    }

    return null;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');

    try {
      setEditingExistingPhoto(false);
      // Read file as data URL for crop modal
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        setCurrentFile(file);
        setImageForCrop(dataUrl);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to read photo file';
      setError(errorMsg);
    }
  };

  const handleCropConfirm = (data: CropData) => {
    // Save crop data for next time modal opens
    setLastCropData(data);

    // If editing existing photo, handle differently
    if (editingExistingPhoto) {
      if (readOnly) {
        setShowCropModal(false);
        setEditingExistingPhoto(false);
        onCropDataChange?.(data);
        setError('');
        onPhotoSaved?.({ photoUrl, cropData: data });
      } else {
        handleExistingPhotoCropConfirm(data);
      }
      return;
    }

    if (!currentFile) return;

    setShowCropModal(false);
    onCropDataChange?.(data);

    setUploading(true);

    try {
      // Read file again for preview and upload
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        setPreviewUrl(dataUrl);
        onPhotoFileChange?.(currentFile);

        if (readOnly) {
          setPhotoUrl('');
          onPhotoUrlChange('');
          setError('');
          onPhotoSaved?.({ photoUrl: '', cropData: data });
        } else if (personId) {
          // If person already exists, upload the photo to server with crop data
          uploadPhotoToServer(currentFile, data);
        } else {
          // For new person, store the file and crop data for parent to handle
          setPhotoUrl('');
          onPhotoUrlChange('');
        }
      };
      reader.readAsDataURL(currentFile);
    } catch (err: any) {
      const errorMsg = err?.message || 'Failed to upload photo';
      setError(errorMsg);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageForCrop('');
    setCurrentFile(null);
    setEditingExistingPhoto(false);
    setUploading(false);
  };

  const uploadPhotoToServer = async (file: File, croppingData?: CropData) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Add crop data if provided
      if (croppingData) {
        formData.append('cropX', croppingData.cropX.toString());
        formData.append('cropY', croppingData.cropY.toString());
        formData.append('cropWidth', croppingData.cropWidth.toString());
        formData.append('cropHeight', croppingData.cropHeight.toString());
        formData.append('cropZoom', croppingData.cropZoom.toString());
      }

      const response = await peopleAPI.uploadPersonPhoto(personId!, formData);
      const newUrl = response.data.data;
      setPhotoUrl(newUrl);
      onPhotoUrlChange(newUrl);
      onPhotoSaved?.({ photoUrl: newUrl, cropData: croppingData || null });
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to upload photo';
      setError(errorMsg);
    }
  };

  const handleUrlChange = (url: string) => {
    setPhotoUrl(url);
    setPreviewUrl(url);
    onPhotoUrlChange(url);
    setError('');
  };

  const handleClearPhoto = () => {
    setPhotoUrl('');
    setPreviewUrl('');
    setCurrentFile(null);
    setLastCropData(null);
    onPhotoUrlChange('');
    onPhotoFileChange?.(null);
    onCropDataChange?.(null);
    onPhotoSaved?.({ photoUrl: '', cropData: null });
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditExistingPhoto = async () => {
    if (!photoUrl || !personId) return;

    setEditingExistingPhoto(true);
    try {
      const dataUrl = await loadPhotoAsDataUrl(photoUrl);
      setImageForCrop(dataUrl);
      setShowCropModal(true);
    } catch (err) {
      setError('Failed to load photo for editing');
    }
  };

  const handleOpenCompactEditor = async () => {
    if (previewUrl || photoUrl) {
      if (personId && photoUrl) {
        await handleEditExistingPhoto();
        return;
      }

      setEditingExistingPhoto(false);
      setImageForCrop(previewUrl);
      setShowCropModal(true);
      return;
    }

    setEditingExistingPhoto(false);
    setImageForCrop('');
    setShowCropModal(true);
  };

  const handleRemoveFromEditor = () => {
    handleClearPhoto();
    setShowCropModal(false);
    setEditingExistingPhoto(false);
    setImageForCrop('');
  };

  const handleExistingPhotoCropConfirm = async (data: CropData) => {
    if (!personId) return;

    setShowCropModal(false);
    setEditingExistingPhoto(false);
    onCropDataChange?.(data);

    // Update crop data on server without re-uploading the photo
    try {
      setUploading(true);
      await peopleAPI.updatePerson(personId, {
        photoCropX: data.cropX,
        photoCropY: data.cropY,
        photoCropWidth: data.cropWidth,
        photoCropHeight: data.cropHeight,
        photoCropZoom: data.cropZoom,
      });
      setError('');
      onPhotoSaved?.({ photoUrl, cropData: data });
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to save crop data';
      setError(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const uploadButtonClassName = 'w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-blue-500 hover:bg-blue-50 disabled:opacity-50 transition';

  return (
    <div className="space-y-4">
      {/* Crop Modal */}
      <ImageCropModal
        isOpen={showCropModal}
        imageSrc={imageForCrop}
        onCropConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
        initialCropData={lastCropData || undefined}
        title={t('photo.imageEditorTitle', { ns: 'common' })}
        onRequestNewImage={() => fileInputRef.current?.click()}
        onRemoveImage={handleRemoveFromEditor}
        canRemoveImage={Boolean(previewUrl || photoUrl)}
        hasImage={Boolean(imageForCrop)}
      />

      {compact && (uploadMode === 'file' || readOnly) && (
        <div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={ALLOWED_EXTENSIONS.join(',')}
            disabled={disabled || uploading || (readOnly && !personId)}
            className="hidden"
          />
          <div className="flex justify-start">
            <div className="group flex w-[104px] flex-col items-stretch gap-2">
              <button
                type="button"
                onClick={handleOpenCompactEditor}
                disabled={disabled || uploading || (readOnly && !personId)}
                className="relative block h-[104px] w-[104px] overflow-hidden rounded-[18px] border border-[#C8C3BB] bg-[linear-gradient(135deg,#EEF4FB_0%,#F6FAFE_55%,#FBF8F3_100%)] shadow-[0_10px_24px_rgba(24,95,165,0.12)] transition hover:-translate-y-[1px] hover:border-[#185FA5] hover:shadow-[0_16px_30px_rgba(24,95,165,0.16)] disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={previewUrl ? 'Change photo' : resolvedUploadLabel}
              >
                {previewUrl ? (
                  <>
                    <img
                      src={previewUrl}
                      alt={t('photo.previewAlt', { ns: 'common' })}
                      className="h-full w-full object-cover"
                      onError={() => setPreviewUrl('')}
                    />
                    {(readOnly || compact) && (
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-center bg-[linear-gradient(180deg,rgba(26,23,20,0)_0%,rgba(26,23,20,0.2)_52%,rgba(26,23,20,0.48)_100%)] px-2 py-2.5">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[rgba(255,255,255,0.28)] bg-[rgba(26,23,20,0.18)] text-white backdrop-blur-[2px]">
                          <svg
                            className="h-3.5 w-3.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path d="M4 8.5h3l1.2-2h7.6l1.2 2H20a1 1 0 0 1 1 1V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.5a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                            <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                          </svg>
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center px-3 text-center">
                    <span className="flex h-11 w-11 items-center justify-center rounded-[20px] border border-[#D8D2C9] bg-[rgba(255,255,255,0.74)] text-[#185FA5] shadow-[0_12px_24px_rgba(24,95,165,0.12)] backdrop-blur-sm">
                      {uploading ? (
                        <svg
                          className="h-5 w-5 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path d="M12 3a9 9 0 1 1-9 9" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path d="M12 15V7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                          <path d="M8.8 10.2L12 7l3.2 3.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M5 16.5v1A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-1" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                      )}
                    </span>
                  </div>
                )}
              </button>

            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      {!compact && previewUrl && (
        <div className={`flex ${compact ? 'justify-start' : 'justify-center'}`}>
          <div className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-300 bg-gray-100">
            <img
              src={previewUrl}
              alt={t('photo.previewAlt', { ns: 'common' })}
              className="w-full h-full object-cover"
              onError={() => setPreviewUrl('')}
            />
            {!uploading && (
              <div className="absolute inset-0 flex items-center justify-center gap-1 opacity-0 hover:opacity-100 bg-black bg-opacity-50 transition">
                {readOnly && personId && photoUrl && (
                  <IconButton
                    type="button"
                    onClick={handleEditExistingPhoto}
                    variant="soft"
                    size="sm"
                    title={t('photo.editCropTitle', { ns: 'common' })}
                  >
                    ✎
                  </IconButton>
                )}
                <IconButton
                  type="button"
                  onClick={handleClearPhoto}
                  variant="danger"
                  size="sm"
                  aria-label={t('photo.clearPhotoAriaLabel', { ns: 'common' })}
                >
                  ✕
                </IconButton>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mode Selector - Hidden in read-only mode */}
      {!readOnly && allowUrlUpload && !hideUrlField && (
        <SegmentedControl
          value={uploadMode}
          onChange={setUploadMode}
          size="sm"
          items={[
            { value: 'file', label: 'Upload File', disabled: disabled || uploading },
            { value: 'url', label: 'Paste URL', disabled: disabled || uploading },
          ]}
        />
      )}

      {/* File Upload Mode - Always show in read-only mode */}
      {!compact && (uploadMode === 'file' || readOnly) ? (
        <div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={ALLOWED_EXTENSIONS.join(',')}
            disabled={disabled || uploading || (readOnly && !personId)}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || uploading || (readOnly && !personId)}
            className={uploadButtonClassName}
          >
            {compact ? (
              <span className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#185FA5] text-white shadow-[0_8px_16px_rgba(24,95,165,0.28)]">
                  {uploading ? (
                    <svg
                      className="h-5 w-5 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 3a9 9 0 1 1-9 9"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 15V6"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M8.5 9.5L12 6l3.5 3.5"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5 16.5v1A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-1"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </span>
                <span className="flex flex-col">
                  <span className="text-[15px] font-semibold leading-none text-[#1A1714]">
                    {uploading ? 'Uploading...' : uploadLabel}
                  </span>
                  <span className="mt-1 text-xs leading-none text-[#6B6560]">
                    JPEG, PNG, WebP
                  </span>
                </span>
              </span>
            ) : (
              <>
                <div className="text-sm font-medium text-gray-700">
                  {uploading ? 'Uploading...' : 'Click to select photo'}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  JPEG, PNG, or WebP • Max 5MB
                </div>
              </>
            )}
          </button>
        </div>
      ) : null}

      {/* URL Display (read-only) or URL Input (editable) */}
      {allowUrlUpload && !hideUrlField && !readOnly && uploadMode === 'url' ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Photo URL
          </label>
          <TextInput
            type="url"
            value={photoUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://example.com/photo.jpg"
            disabled={disabled || uploading}
          />
        </div>
      ) : null}

      {/* URL Display - Read-only in edit mode */}
      {allowUrlUpload && !hideUrlField && readOnly && photoUrl && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Photo URL
          </label>
          <TextInput
            type="text"
            value={photoUrl}
            readOnly
            disabled
            className="bg-gray-50 text-gray-600 opacity-75"
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className={`rounded border border-red-200 bg-red-50 px-3 py-2 ${formErrorTextClassName}`}>
          {error}
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          Uploading photo...
        </div>
      )}
    </div>
  );
};
